import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Activity, CheckCircle, XCircle, AlertTriangle, Wrench, RefreshCw,
  Loader2, Wifi, Shield, Clock, Zap, Copy, Server
} from "lucide-react";

interface DiagStep {
  id: string;
  label: string;
  status: "ok" | "warn" | "error" | "fixed" | "skipped";
  message: string;
  details?: string;
  fixApplied?: string;
}

interface DiagResult {
  success: boolean;
  healthy: boolean;
  fixedCount: number;
  steps: DiagStep[];
  timestamp: string;
  vps_ip: string;
  resolved_vps_url?: string;
  error?: string;
}

const statusIcon = (status: string) => {
  switch (status) {
    case "ok": return <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />;
    case "fixed": return <Wrench className="w-4 h-4 text-blue-500 shrink-0" />;
    case "warn": return <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />;
    case "error": return <XCircle className="w-4 h-4 text-destructive shrink-0" />;
    default: return <Clock className="w-4 h-4 text-muted-foreground shrink-0" />;
  }
};

const statusBorder = (status: string) => {
  switch (status) {
    case "ok": return "border-green-500/30 bg-green-500/5";
    case "fixed": return "border-blue-500/30 bg-blue-500/5";
    case "warn": return "border-yellow-500/30 bg-yellow-500/5";
    case "error": return "border-destructive/30 bg-destructive/5";
    default: return "border-border bg-secondary/30";
  }
};

const AdminBinanceDiagnostics = () => {
  const { toast } = useToast();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DiagResult | null>(null);
  const [autoFix, setAutoFix] = useState(true);
  const [autoRun, setAutoRun] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runDiagnostics = async () => {
    setRunning(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || "";

      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/binance-diagnostics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "",
          },
          body: JSON.stringify({ autoFix }),
        },
      );
      const data: DiagResult = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || "فشل التشخيص");
      }

      setResult(data);

      if (data.healthy) {
        toast({ title: "✅ النظام يعمل بشكل كامل", description: data.fixedCount > 0 ? `تم إصلاح ${data.fixedCount} مشكلة تلقائياً` : "جميع الفحوصات ناجحة" });
      } else {
        const errors = data.steps.filter(s => s.status === "error").length;
        const warns = data.steps.filter(s => s.status === "warn").length;
        toast({
          title: "⚠️ يوجد مشاكل تحتاج انتباه",
          description: `${errors} خطأ | ${warns} تحذير${data.fixedCount > 0 ? ` | ${data.fixedCount} تم إصلاحه` : ""}`,
          variant: "destructive",
        });
      }
    } catch (e: any) {
      toast({ title: "خطأ في التشخيص", description: e.message, variant: "destructive" });
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (autoRun) {
      runDiagnostics();
      intervalRef.current = setInterval(runDiagnostics, 60000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRun]);

  const copyIp = () => {
    const ip = result?.vps_ip || "74.81.81.81";
    navigator.clipboard.writeText(ip);
    toast({ title: "تم النسخ ✅", description: ip });
  };

  const healthColor = result
    ? result.healthy ? "border-green-500/50" : "border-destructive/50"
    : "border-border";

  return (
    <Card className={`glass border-2 ${healthColor} transition-colors`}>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Activity className="w-5 h-5 text-primary" />
            تشخيص Binance الذكي
            {result && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${result.healthy ? "bg-green-500/20 text-green-400" : "bg-destructive/20 text-destructive"}`}>
                {result.healthy ? "سليم" : "يحتاج إصلاح"}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">إصلاح تلقائي</label>
              <Switch checked={autoFix} onCheckedChange={setAutoFix} />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">مراقبة مستمرة</label>
              <Switch checked={autoRun} onCheckedChange={setAutoRun} />
            </div>
            <Button onClick={runDiagnostics} disabled={running} size="sm">
              {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
              {running ? "جاري الفحص..." : "فحص وإصلاح"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          يفحص اتصال VPS → Binance تلقائياً ويصلح المشاكل القابلة للإصلاح (عناوين ناقصة، إعدادات خاطئة)
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* VPS IP Banner */}
        <div className="flex items-center justify-between p-3 rounded-lg border border-primary/40 bg-primary/5">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">IP الثابت لـ Binance Whitelist</p>
              <p className="text-[10px] text-muted-foreground">أضف هذا الـ IP في إعدادات API على Binance</p>
              {result?.resolved_vps_url && (
                <p className="text-[10px] text-muted-foreground break-all mt-1">الرابط الفعلي المكتشف: {result.resolved_vps_url}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="text-lg font-bold font-mono text-primary bg-primary/20 px-3 py-1 rounded">
              {result?.vps_ip || "74.81.81.81"}
            </code>
            <Button variant="outline" size="sm" onClick={copyIp}>
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-2">
            {/* Summary bar */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground p-2 rounded bg-secondary/30">
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> {result.steps.filter(s => s.status === "ok").length} ناجح</span>
              <span className="flex items-center gap-1"><Wrench className="w-3 h-3 text-blue-500" /> {result.steps.filter(s => s.status === "fixed").length} تم إصلاحه</span>
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-yellow-500" /> {result.steps.filter(s => s.status === "warn").length} تحذير</span>
              <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-destructive" /> {result.steps.filter(s => s.status === "error").length} خطأ</span>
              <span className="mr-auto" />
              <span><Clock className="w-3 h-3 inline mr-1" />{new Date(result.timestamp).toLocaleTimeString("ar-EG")}</span>
            </div>

            {/* Steps */}
            {result.steps.map((step) => (
              <div key={step.id} className={`p-3 rounded-lg border ${statusBorder(step.status)} transition-all`}>
                <div className="flex items-start gap-2">
                  {statusIcon(step.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{step.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        step.status === "ok" ? "bg-green-500/20 text-green-400" :
                        step.status === "fixed" ? "bg-blue-500/20 text-blue-400" :
                        step.status === "warn" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-destructive/20 text-destructive"
                      }`}>
                        {step.status === "ok" ? "✅" : step.status === "fixed" ? "🔧" : step.status === "warn" ? "⚠️" : "❌"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{step.message}</p>
                    {step.details && (
                      <p className="text-[10px] text-muted-foreground/70 mt-1 border-t border-border/30 pt-1">{step.details}</p>
                    )}
                    {step.fixApplied && (
                      <p className="text-[10px] text-blue-400 mt-1 flex items-center gap-1">
                        <Wrench className="w-3 h-3" /> {step.fixApplied}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!result && !running && (
          <div className="text-center py-8 text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">اضغط "فحص وإصلاح" لبدء تشخيص الاتصال بـ Binance</p>
            <p className="text-xs mt-1">سيتم فحص VPS، مفاتيح API، الأرصدة، العناوين وإصلاح أي مشاكل تلقائياً</p>
          </div>
        )}

        {running && !result && (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">جاري فحص جميع مكونات الاتصال...</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBinanceDiagnostics;
